import { lazy, Suspense, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
/**
 * App Router
 *
 * React Router를 사용한 라우팅 설정
 * - 공개 라우트: 로그인 페이지
 * - 보호된 라우트: 인증 필요한 페이지들
 *
 * PRJ-001: 프로젝트 선택/생성 페이지가 메인 진입점
 */

import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate, useRouteError } from 'react-router-dom';
import { SIMULATION_RUN_PATH } from '@/app/routes';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { PublicOnlyRoute } from '@/features/auth/PublicOnlyRoute';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import LandingPage from './pages/LandingPage';
import PlatformHomePage from './pages/PlatformHomePage';
import ProjectPickerPage from './pages/ProjectPickerPage';
import DocumentTitle from '@/app/chrome/DocumentTitle';
import { QUICKRUN_PROJECT_ID } from '@/app/quickRunProject';

/**
 * 라우트 청크 선언 — 한 곳(트랙 A). `lazyPage` 가 이 표에서 라우트 요소를 만든다.
 * 유휴 선적재(모든 청크 import)는 실측으로 기각했다: 문서마다 4MB 를 평가해 힙 +40MB·떠날 때 최대 2초 정지(§5).
 * 진입 페이지(Login·Signup·Landing·PlatformHome·ProjectPicker)만 정적이고 나머지는 전부 여기 있어야 한다(routeChunks 검출기).
 */
const ROUTE_CHUNKS = {
  AnnouncementsPage: () => import('./pages/AnnouncementsPage'),
  IssuesPage: () => import('./pages/IssuesPage'),
  ProjectHomePage: () => import('./pages/ProjectHomePage'),
  ModelHomePage: () => import('./pages/ModelHomePage'),
  EditorShell: () => import('./pages/EditorShell'),
  SimulationPage: () => import('./pages/SimulationPage'),
  AccountSettingsPage: () => import('./pages/AccountSettingsPage'),
  ThemePreviewPage: () => import('./pages/ThemePreviewPage'),
  QuickRunPage: () => import('./pages/QuickRunPage'),
  SimulationsPage: () => import('./pages/SimulationsPage'),
  TutorialPage: () => import('./pages/TutorialPage'),
  TutorialDetailPage: () => import('./pages/TutorialDetailPage'),
  ComponentViewerDemo: () => import('@/features/project-picker/ComponentViewerDemo'),
} as const;

type RouteChunks = typeof ROUTE_CHUNKS;
type PageOf<K extends keyof RouteChunks> = Awaited<ReturnType<RouteChunks[K]>>['default'];
/** 표의 항목 하나를 라우트 요소로 — 페이지의 props 타입은 그대로 유지된다 */
const lazyPage = <K extends keyof RouteChunks>(name: K) => lazy(ROUTE_CHUNKS[name] as () => Promise<{ default: PageOf<K> }>);

const AnnouncementsPage = lazyPage('AnnouncementsPage');
const IssuesPage = lazyPage('IssuesPage');
const ProjectHomePage = lazyPage('ProjectHomePage');
const ModelHomePage = lazyPage('ModelHomePage');
const EditorShell = lazyPage('EditorShell');
const SimulationPage = lazyPage('SimulationPage');
const AccountSettingsPage = lazyPage('AccountSettingsPage');
const ThemePreviewPage = lazyPage('ThemePreviewPage');
const QuickRunPage = lazyPage('QuickRunPage');
const SimulationsPage = lazyPage('SimulationsPage');
const TutorialPage = lazyPage('TutorialPage');
const TutorialDetailPage = lazyPage('TutorialDetailPage');
const ComponentViewerDemo = lazyPage('ComponentViewerDemo');

/**
 * 라우트 청크를 받는 동안의 자리 — 직접 진입·새로고침에서 보인다. 앱 내 전환은 react-router 7 이 startTransition 으로
 * 갱신하므로 이전 화면이 남고 이 자리는 나오지 않는다(첫 방문 페이지의 청크 적재 동안 피드백 없음 — 계획서 §5).
 * 인증 확인 화면(ProtectedRoute)과 같은 크기의 스피너, 레이아웃 이동 없음.
 */
function RouteChunkFallback() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <CircularProgress size={48} />
    </Box>
  );
}

function OperationHmiRedirect() {
  useEffect(() => {
    window.location.replace('/operation-hmi/');
  }, []);
  return <RouteChunkFallback />;
}

/**
 * 라우트 렌더 오류의 마지막 자리 — 청크 적재 실패(main.tsx 의 1회 자동 재적재 가드가 이미 소진된 뒤)나 렌더 예외를
 * react-router 의 기본 오류 화면("Unexpected Application Error!") 대신 앱 모양의 화면과 다시 불러오기 버튼으로 받는다.
 * 데이터 라우터의 errorElement 로 루트에 한 번 선언한다.
 */
function RouteErrorFallback() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2, p: 3 }}>
      <Typography variant="h6">화면을 불러오지 못했습니다</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, textAlign: 'center', wordBreak: 'break-all' }}>{message}</Typography>
      <Button variant="contained" onClick={() => window.location.reload()}>다시 불러오기</Button>
    </Box>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<DocumentTitle />} errorElement={<RouteErrorFallback />}>
      {/* 공개 라우트 — 로그인된 사용자는 원래 자리(state.from, 없으면 /home → /)로 보낸다. 판정은 PublicOnlyRoute 한 곳 */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnlyRoute>
            <SignupPage />
          </PublicOnlyRoute>
        }
      />

      {/* 보호된 라우트 - 인증 필요 */}
      {/* 최상위 랜딩(2026-08-11) — 이중 정체성 2택: 플랫폼(/projects) vs 별도 운전 HMI */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <LandingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform"
        element={
          <ProtectedRoute>
            <PlatformHomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/issues"
        element={
          <ProtectedRoute>
            <IssuesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements/:number"
        element={
          <ProtectedRoute>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />
      {/* 설정은 플랫폼 공용 화면이므로 옛 주소도 그대로 승계한다. 나머지 운전 하위 경로는
          HMI의 plant/run 경로와 호환되지 않아 홈으로 수렴한다. */}
      <Route path="/operation/settings" element={<Navigate to="/settings" replace />} />
      <Route path="/operation/*" element={<OperationHmiRedirect />} />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectPickerPage />
          </ProtectedRoute>
        }
      />

      {/* FEAT-quickrun-page: legacy /projects/_quickrun → /quickrun 리다이렉트.
          라우트는 항상 등록하고 노출 여부는 QuickRunPage가 판정한다(env 플래그
          또는 개발자 모드) — 라우터는 모듈 로드 시 1회 생성이라 런타임 토글을
          여기서 반영할 수 없다. */}
      <Route path={`/projects/${QUICKRUN_PROJECT_ID}`} element={<Navigate to="/quickrun" replace />} />
      <Route path={`/projects/${QUICKRUN_PROJECT_ID}/*`} element={<Navigate to="/quickrun" replace />} />

      {/* MAIN-001: Project Home Page */}
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <ProjectHomePage />
          </ProtectedRoute>
        }
      />

      {/* MDH-001: Model Home Page */}
      <Route
        path="/projects/:projectId/models/:modelId"
        element={
          <ProtectedRoute>
            <ModelHomePage />
          </ProtectedRoute>
        }
      />

      {/* FEAT-quickrun-page: 전용 페이지 — 노출 판정은 QuickRunPage 내부(위 주석) */}
      <Route
        path="/quickrun"
        element={
          <ProtectedRoute>
            <QuickRunPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tutorial"
        element={
          <ProtectedRoute>
            <TutorialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tutorial/:tutorialId"
        element={
          <ProtectedRoute>
            <TutorialDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Legacy routes — 홈 계열은 랜딩으로, 워크스페이스 계열은 픽커로 */}
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/" replace />} />
      <Route path="/workspace" element={<Navigate to="/projects" replace />} />

      <Route
        path="/editor"
        element={
          <ProtectedRoute>
            <EditorShell />
          </ProtectedRoute>
        }
      />
      {/* Modelica 모델러 진입 별칭 — 종류-무관 EditorShell이 활성 모델 종류로 본문 결정.
          (editorPathForModel이 FMU 모델에 이 경로를 반환 — 외부 진입 링크 호환) */}
      <Route
        path="/modelica-editor"
        element={
          <ProtectedRoute>
            <EditorShell />
          </ProtectedRoute>
        }
      />
      <Route
        path={SIMULATION_RUN_PATH}
        element={
          <ProtectedRoute>
            <SimulationPage />
          </ProtectedRoute>
        }
      />
      {/* 시뮬레이션 관리 — 계정의 세션 목록·이어보기·중지 (#288) */}
      <Route
        path="/simulations"
        element={
          <ProtectedRoute>
            <SimulationsPage />
          </ProtectedRoute>
        }
      />

      {/* Account Settings Page — 세계 무관, 사이드바 없음, 돌아가기=들어온 자리 */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Component Viewer Demo (Dashboard 개편용) */}
      <Route
        path="/component-demo"
        element={
          <ProtectedRoute>
            <ComponentViewerDemo />
          </ProtectedRoute>
        }
      />

      {/* Theme Preview (디자인 시스템 개발용 - 인증 불필요) */}
      <Route path="/theme-preview" element={<ThemePreviewPage />} />

      {/* 관리자 라우트 → 계정 설정 페이지로 리다이렉트 */}
      <Route path="/admin" element={<Navigate to="/settings" replace />} />

      {/* 404 — 늘 /login 으로. 로그인된 사용자는 PublicOnlyRoute 가 원래 가려던 곳(없으면 /home → /)으로 보낸다 */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Route>
  )
);

const AppRouter: React.FC = () => {
  return <Suspense fallback={<RouteChunkFallback />}><RouterProvider router={router} /></Suspense>;
};

export default AppRouter;
